import { ArrowUp, Bot, ChevronRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useSidebar } from '../../contexts/SidebarContext';
import { useEntityMissions, type EntityMission, type MissionEntityKind } from '../../hooks/useEntityMissions';
import { useNewMission } from '../../hooks/useNewMission';
import { NEW_MISSION_MIN } from '../../lib/bureau/newMission';
import { missionStatusLabel } from '../../lib/mission/missionStatus';
import { cockpitHref } from '../../lib/workQueue/cockpitView';
import { ACCENT_FILL } from '../bureau/buttons';
import { Pastille, SheetBlock, SheetEmpty } from './sheet/EncreSheet';

type Named = { id: string; name: string };

/** What one might ask Otto, by kind of sheet. */
const PLACEHOLDER: Record<MissionEntityKind, string> = {
  account: 'Prépare la relance du devis de groupe…',
  contact: "Relance-le jeudi s'il n'a pas répondu…",
  opportunity: 'Prépare la proposition pour le rendez-vous de mardi…',
};


/** « t'attend », « reprend le 9 oct. », or the mission's state. */
function stateOf(m: EntityMission): string {
  if (m.status === 'awaiting_human') return "t'attend";
  if (m.next_wakeup_at) {
    const d = new Date(m.next_wakeup_at);
    if (!Number.isNaN(d.getTime())) return `reprend le ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
  }
  return missionStatusLabel(m.status).toLowerCase();
}

/**
 * On an account's, a contact's or a deal's sheet (mock-up « Fiche »): the missions Otto leads about it, each
 * opening in the Bureau, then « Demander à Otto » — a request that becomes a mission tied to this
 * sheet (the same door as « Nouvelle mission », `useNewMission`), opened in the Bureau where Otto's
 * work shows as it goes.
 */
export function EntityMissionsBlock({
  kind,
  entity,
  account = null,
}: {
  kind: MissionEntityKind;
  entity: Named;
  /** A contact's or a deal's account, named in the mission's objective. */
  account?: Named | null;
}) {
  const navigate = useNavigate();
  const { closeSidebar } = useSidebar();
  const { missions, loading, error } = useEntityMissions(kind, entity.id);
  const { create, pending, error: createError } = useNewMission();
  const [text, setText] = useState('');
  const ready = text.trim().length >= NEW_MISSION_MIN && !pending;

  const submit = async () => {
    if (!ready) return;
    const created = await create({
      text,
      account: kind === 'account' ? entity : account,
      contact: kind === 'contact' ? entity : null,
      opportunity: kind === 'opportunity' ? entity : null,
      procedure: null,
      driveFileIds: [],
    });
    if (!created) return;
    setText('');
    closeSidebar();
    navigate(cockpitHref('attente', { kind: 'item', source: created.source, sourceId: created.sourceId }));
  };

  return (
    <>
      <SheetBlock title="Missions liées" count={missions.length}>
        {loading && missions.length === 0 && <SheetEmpty>Chargement…</SheetEmpty>}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {!loading && !error && missions.length === 0 && <SheetEmpty>Aucune mission en cours avec {entity.name}.</SheetEmpty>}
        {missions.map((m) => (
          <Link
            key={m.thread_id}
            to={cockpitHref('attente', { kind: 'item', source: m.source, sourceId: m.source_id })}
            onClick={closeSidebar}
            className="-mx-2.5 flex min-w-0 items-center gap-3 rounded-[10px] px-2.5 py-2 transition-colors hover:bg-desk-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus"
          >
            <Pastille icon={Bot} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-desk-ink">{m.title}</span>
              <span className={`block text-xs ${m.status === 'awaiting_human' ? 'text-desk-signal' : 'text-desk-soft'}`}>{stateOf(m)}</span>
            </span>
            <ChevronRight size={16} className="shrink-0 text-desk-faint" aria-hidden="true" />
          </Link>
        ))}
      </SheetBlock>

      <SheetBlock title="Demander à Otto">
        <div className="rounded-[24px] bg-desk-card px-3 py-2.5 shadow-lift focus-within:ring-2 focus-within:ring-desk-focus">
          <label htmlFor={`ask-text-${entity.id}`} className="sr-only">
            Demander à Otto à propos de {entity.name}
          </label>
          <textarea
            id={`ask-text-${entity.id}`}
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder={PLACEHOLDER[kind]}
            className="w-full resize-none border-0 bg-transparent px-1 py-1 text-sm leading-6 text-desk-ink placeholder:text-desk-muted focus:outline-none focus:ring-0"
          />
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!ready}
              title={ready ? 'Confier à Otto (Ctrl+Entrée)' : `Au moins ${NEW_MISSION_MIN} caractères`}
              aria-label="Confier à Otto"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus disabled:cursor-not-allowed disabled:opacity-30 ${ACCENT_FILL}`}
            >
              {pending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <ArrowUp size={18} aria-hidden="true" />}
            </button>
          </div>
        </div>
        {createError && (
          <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
            {createError}
          </p>
        )}
        <p className="mt-2.5 text-xs text-desk-muted">La mission s'ouvre avec cette fiche jointe, dans le Bureau.</p>
      </SheetBlock>
    </>
  );
}
